// Empire.tsx — The 4X HUD shown while playing a match: your empire's status bar, the
// scoreboard + event log, the host's pace control, your fleet list (select ships, then
// send them from a planet sheet), each world's ownership/defences/shipyard, and the
// end-of-match banner. Everything reads the server-authoritative snapshot; every action
// goes out as a validated Command over the match socket.
import { useMemo, useState } from 'react';
import type { Galaxy, Planet } from '../types';
import { useGame, useMatch, useSim } from '../state/context';
import {
  isEmpireShip,
  MIN_YARD_POPS,
  ownerColor,
  ownerName,
  SHIP_CLASSES,
  type Ship,
  type SimState,
  type World,
} from '../state/economy';
import { gotoMatch } from './Lobby';

const YEAR = 31557600;
const DAY = 86400;

export function formatCredits(n: number): string {
  const a = Math.abs(n);
  const s = a >= 1e6 ? `${(a / 1e6).toFixed(1)}M` : a >= 1e4 ? `${(a / 1e3).toFixed(0)}k` : a >= 1e3 ? `${(a / 1e3).toFixed(1)}k` : a.toFixed(0);
  return `${n < 0 ? '−' : ''}§${s}`;
}

function formatPopsShort(p: number): string {
  if (p >= 1e6) return `${(p / 1e6).toFixed(1)}M`;
  if (p >= 1e3) return `${(p / 1e3).toFixed(1)}k`;
  return p.toFixed(0);
}

function planetLookup(galaxy: Galaxy): Map<string, { planet: Planet; systemId: string; systemName: string }> {
  const m = new Map<string, { planet: Planet; systemId: string; systemName: string }>();
  for (const s of galaxy.systems) for (const p of s.planets) m.set(p.id, { planet: p, systemId: s.id, systemName: s.name });
  return m;
}

function totalPops(sim: SimState): number {
  return (sim.worlds ?? []).reduce((a, w) => a + w.pops, 0);
}

function kindLabel(kind: string): string {
  return SHIP_CLASSES.find((c) => c.kind === kind)?.label ?? kind;
}

// ─── top bar ───

export function EmpireBar() {
  const sim = useSim();
  const match = useMatch();
  if (!sim || !match) return null;
  const me = (sim.empires ?? []).find((e) => e.id === match.you);
  const year = Math.floor(sim.gameTime / YEAR);
  const limit = sim.timeLimit ? Math.round(sim.timeLimit / YEAR) : 0;
  if (!me) {
    return (
      <div className="empire-bar">
        <span className="empire-name">Spectating · {match.name}</span>
        {limit ? <span className="empire-stat">Year {year} / {limit}</span> : null}
      </div>
    );
  }
  const share = totalPops(sim) > 0 ? me.pops / totalPops(sim) : 0;
  const goal = sim.victoryShare || 0.4;
  return (
    <div className="empire-bar">
      <span className="empire-swatch" style={{ background: me.color }} />
      <span className="empire-name">{me.name}</span>
      <span className="empire-stat" title="Treasury (net income per day)">
        {formatCredits(me.treasury)}{' '}
        <span className={me.income >= 0 ? 'gain' : 'loss'}>
          {me.income >= 0 ? '+' : ''}
          {me.income.toFixed(0)}/d
        </span>
      </span>
      <span className="empire-stat">{me.worlds} worlds</span>
      <span className="empire-stat">{formatPopsShort(me.pops)} pops</span>
      <span className="empire-stat">{me.ships} ships</span>
      <span className="victory" title={`Hold ${Math.round(goal * 100)}% of the galaxy's population to win`}>
        <span className="victory-fill" style={{ width: `${Math.min(100, (share / goal) * 100)}%`, background: me.color }} />
        <span className="victory-label">
          {(share * 100).toFixed(1)}% / {Math.round(goal * 100)}%
        </span>
      </span>
      {limit ? <span className="empire-stat">Year {year} / {limit}</span> : null}
    </div>
  );
}

// ─── pace (host only) ───

const PACE = [
  { label: '⏸', mult: 0 },
  { label: '½×', mult: 0.5 },
  { label: '1×', mult: 1 },
  { label: '2×', mult: 2 },
  { label: '4×', mult: 4 },
];

export function MatchSpeed() {
  const sim = useSim();
  const match = useMatch();
  const { setSpeed } = useGame();
  if (!match) return null;
  const cur = sim ? sim.speed / match.baseSpeed : 1;
  return (
    <div className="speed">
      <span className="speed-label">{match.isHost ? 'PACE' : 'PACE (host)'}</span>
      {PACE.map((p) => (
        <button
          key={p.label}
          type="button"
          className={Math.abs(cur - p.mult) < 0.01 ? 'speed-btn active' : 'speed-btn'}
          disabled={!match.isHost || !!sim?.over}
          onClick={() => setSpeed(p.mult * match.baseSpeed)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// ─── scoreboard + events ───

export function Scoreboard({ galaxy }: { galaxy: Galaxy }) {
  const sim = useSim();
  const match = useMatch();
  const { focus } = useGame();
  const planets = useMemo(() => planetLookup(galaxy), [galaxy]);
  if (!sim || !match) return null;
  const total = totalPops(sim);
  const empires = [...(sim.empires ?? [])].sort((a, b) => b.pops - a.pops);
  const events = [...(sim.events ?? [])].reverse().slice(0, 6);
  return (
    <div className="scoreboard">
      <ul className="score-list">
        {empires.map((e) => (
          <li key={e.id} className={e.alive ? 'score-row' : 'score-row dead'}>
            <span className="dot" style={{ background: e.color }} />
            <span className="score-name">
              {e.name}
              {e.id === match.you ? ' (you)' : e.ai ? ' · AI' : ''}
            </span>
            <span className="score-meta">{e.alive ? `${e.worlds}w · ${total ? ((100 * e.pops) / total).toFixed(0) : 0}%` : 'eliminated'}</span>
          </li>
        ))}
      </ul>
      {events.length > 0 ? (
        <ul className="event-list">
          {events.map((ev, i) => {
            const at = ev.planetId ? planets.get(ev.planetId) : undefined;
            const col = ownerColor(sim, ev.empires?.[0] ?? '');
            return (
              <li
                key={`${ev.time}-${i}`}
                className={at ? 'event-row link' : 'event-row'}
                onClick={at ? () => focus(at.planet.id) : undefined}
              >
                <span className="event-time">{Math.floor(ev.time / YEAR) + 2200}</span>
                <span className="event-text" style={{ borderLeftColor: col }}>
                  {ev.text}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

// ─── fleet ───

function shipStatus(ship: Ship, planets: ReturnType<typeof planetLookup>, galaxy: Galaxy): string {
  const at = (id: string) => planets.get(id)?.planet.name ?? id;
  if (ship.phase === 'orbit') return `at ${at(ship.originId)}`;
  if (ship.phase === 'hyperlane') return `jumping → ${galaxy.systems.find((s) => s.id === ship.destId)?.name ?? ship.destId}`;
  return `→ ${at(ship.targetId)}`;
}

export function FleetPanel({ galaxy }: { galaxy: Galaxy }) {
  const sim = useSim();
  const match = useMatch();
  const { selectShip } = useGame();
  const [open, setOpen] = useState(true);
  const planets = useMemo(() => planetLookup(galaxy), [galaxy]);
  if (!sim || !match || !match.you) return null;
  const mine = (sim.ships ?? []).filter((s) => s.owner === match.you);
  const sel = new Set(match.selection.filter((id) => mine.some((s) => s.id === id)));
  const toggle = (id: string) => {
    const next = new Set(sel);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    match.setSelection([...next]);
  };
  const order = ['cruiser', 'frigate', 'colony'];
  mine.sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind) || a.id.localeCompare(b.id));
  return (
    <div className="fleet-panel">
      <div className="fleet-head">
        <button type="button" className="fleet-title" onClick={() => setOpen(!open)}>
          {open ? '▾' : '▸'} Fleet · {mine.length}
        </button>
        {sel.size > 0 ? <span className="fleet-sel">{sel.size} selected</span> : null}
      </div>
      {open ? (
        <>
          <div className="fleet-tools">
            <button type="button" className="speed-btn" onClick={() => match.setSelection(mine.filter((s) => (s.attack ?? 0) > 0).map((s) => s.id))}>
              Warships
            </button>
            <button type="button" className="speed-btn" onClick={() => match.setSelection(mine.filter((s) => s.kind === 'colony').map((s) => s.id))}>
              Colony ships
            </button>
            <button type="button" className="speed-btn" onClick={() => match.setSelection([])}>
              Clear
            </button>
          </div>
          <ul className="fleet-list">
            {mine.map((s) => (
              <li key={s.id} className={sel.has(s.id) ? 'fleet-row selected' : 'fleet-row'}>
                <button type="button" className="fleet-check" aria-label={`Select ${s.name}`} onClick={() => toggle(s.id)}>
                  {sel.has(s.id) ? '■' : '□'}
                </button>
                <span className="fleet-name" onClick={() => selectShip(s.id, s.phase === 'hyperlane' ? undefined : s.systemId)}>
                  {s.name ?? kindLabel(s.kind)}
                </span>
                <span className="fleet-meta">{shipStatus(s, planets, galaxy)}</span>
                {s.maxHp ? (
                  <span className="hp">
                    <span className="hp-fill" style={{ width: `${(100 * (s.hp ?? 0)) / s.maxHp}%` }} />
                  </span>
                ) : null}
              </li>
            ))}
            {mine.length === 0 ? <li className="fleet-row empty">No ships — build some at your worlds.</li> : null}
          </ul>
          {sel.size > 0 ? <div className="fleet-hint">Open any planet and press “Send here”.</div> : null}
        </>
      ) : null}
    </div>
  );
}

// ─── orders from a planet sheet ───

/** "Send N selected ships here" — shared by the planet sheet and the system roster. */
export function SendHere({ planetId, compact }: { planetId: string; compact?: boolean }) {
  const match = useMatch();
  const sim = useSim();
  const [msg, setMsg] = useState('');
  if (!match || !match.you || match.selection.length === 0 || sim?.over) return null;
  const send = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const err = await match.issue({ type: 'move', shipIds: match.selection, targetId: planetId });
    setMsg(err ?? '');
    if (!err) match.setSelection([]);
  };
  if (compact) {
    return (
      <button type="button" className="send-btn" onClick={send} title="Send the selected ships here">
        Send
      </button>
    );
  }
  return (
    <>
      <button type="button" className="travel-btn" onClick={send}>
        Send {match.selection.length} selected ship{match.selection.length === 1 ? '' : 's'} here →
      </button>
      {msg ? <div className="order-error">{msg}</div> : null}
    </>
  );
}

// harsh kinds settlers can dome over (mirrors sim.harshHabitability); gas giants, molten
// and shattered worlds are never colonizable.
const UNLIVABLE = new Set(['gas', 'molten', 'broken']);

/** The ownership / defence / shipyard block of a planet sheet (matches only). */
export function WorldSection({ planet }: { planet: Planet }) {
  const sim = useSim();
  const match = useMatch();
  const [msg, setMsg] = useState('');
  if (!sim || !match) return null;
  const world: World | undefined = (sim.worlds ?? []).find((w) => w.planetId === planet.id);
  const me = (sim.empires ?? []).find((e) => e.id === match.you);
  const mineHere = world && world.owner === match.you && !!match.you;
  const orbiting = (sim.ships ?? []).filter((s) => isEmpireShip(s) && s.phase === 'orbit' && s.originId === planet.id);
  const issue = async (cmd: Parameters<typeof match.issue>[0]) => setMsg((await match.issue(cmd)) ?? '');

  return (
    <>
      <div className="planet-count">Control</div>
      <dl className="stat-list">
        <div className="stat">
          <dt>Held by</dt>
          <dd>
            {world ? (
              <>
                <span className="dot inline" style={{ background: ownerColor(sim, world.owner) }} /> {ownerName(sim, world.owner)}
              </>
            ) : UNLIVABLE.has(planet.kind) ? (
              'Unlivable'
            ) : (
              'Unclaimed — send a colony ship'
            )}
          </dd>
        </div>
        {world ? (
          <>
            <div className="stat">
              <dt>Population</dt>
              <dd>
                {formatPopsShort(world.pops)} / {formatPopsShort(world.capacity)}
              </dd>
            </div>
            <div className="stat">
              <dt>Defences</dt>
              <dd>
                <span className="hp wide">
                  <span className="hp-fill" style={{ width: `${(100 * world.defense) / Math.max(1, world.maxDefense)}%` }} />
                </span>{' '}
                {world.defense.toFixed(0)} / {world.maxDefense.toFixed(0)}
                {world.besieged ? <span className="loss"> · under siege</span> : null}
              </dd>
            </div>
          </>
        ) : null}
      </dl>
      {orbiting.length > 0 ? (
        <>
          <div className="planet-count">In orbit</div>
          <ul className="moon-list">
            {orbiting.map((s) => (
              <li key={s.id} className="moon-row" style={{ cursor: 'default' }}>
                <span className="dot" style={{ background: ownerColor(sim, s.owner) }} />
                <span className="mname">{s.name ?? kindLabel(s.kind)}</span>
                <span className="mmeta">
                  {ownerName(sim, s.owner)} · {Math.round(s.hp ?? 0)} hp
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {mineHere && world ? (
        <>
          <div className="planet-count">Shipyard</div>
          {(world.queue ?? []).length > 0 ? (
            <ul className="moon-list">
              {(world.queue ?? []).map((b, i) => (
                <li key={i} className="moon-row" style={{ cursor: 'default' }}>
                  <span className="mname">{kindLabel(b.kind)}</span>
                  <span className="mmeta">{i === 0 ? `${Math.round((100 * b.progress) / b.days)}%` : 'queued'}</span>
                  <button type="button" className="send-btn" onClick={() => issue({ type: 'cancelBuild', planetId: planet.id, index: i })}>
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {world.pops < MIN_YARD_POPS ? (
            <div className="readout">Needs {MIN_YARD_POPS.toLocaleString()} pops to build ships.</div>
          ) : (
            <div className="build-grid">
              {SHIP_CLASSES.map((c) => (
                <button
                  key={c.kind}
                  type="button"
                  className="build-btn"
                  disabled={!me || me.treasury < c.cost || (world.queue ?? []).length >= 5 || sim.over}
                  title={`${c.blurb} ${c.days} days · upkeep §${c.upkeep}/day`}
                  onClick={() => issue({ type: 'build', planetId: planet.id, kind: c.kind })}
                >
                  <span className="build-name">{c.label}</span>
                  <span className="build-cost">
                    {formatCredits(c.cost)} · {Math.round(c.days / 30)} mo
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      ) : null}
      <SendHere planetId={planet.id} />
      {msg ? <div className="order-error">{msg}</div> : null}
    </>
  );
}

// ─── ship detail (empire ships) ───

export function EmpireShipPane({ galaxy, ship }: { galaxy: Galaxy; ship: Ship }) {
  const sim = useSim();
  const match = useMatch();
  const { selectShip } = useGame();
  const planets = useMemo(() => planetLookup(galaxy), [galaxy]);
  const mine = !!match?.you && ship.owner === match.you;
  const selected = !!match?.selection.includes(ship.id);
  const days = ship.phase === 'hyperlane' ? Math.max(0, (ship.arriveTime - (sim?.gameTime ?? 0)) / DAY) : 0;
  return (
    <aside className="system-panel">
      <button className="back-btn" onClick={() => selectShip(null)}>
        ← Close
      </button>
      <h1>{ship.name ?? kindLabel(ship.kind)}</h1>
      <div className="spectral" style={{ color: ownerColor(sim, ship.owner) }}>
        {kindLabel(ship.kind)} · {ownerName(sim, ship.owner)}
      </div>
      <dl className="stat-list">
        <div className="stat">
          <dt>Status</dt>
          <dd>{shipStatus(ship, planets, galaxy)}</dd>
        </div>
        {ship.phase === 'hyperlane' ? (
          <div className="stat">
            <dt>Jump ends in</dt>
            <dd>{days.toFixed(0)} days</dd>
          </div>
        ) : null}
        <div className="stat">
          <dt>Hull</dt>
          <dd>
            {Math.round(ship.hp ?? 0)} / {Math.round(ship.maxHp ?? 0)}
          </dd>
        </div>
        <div className="stat">
          <dt>Firepower</dt>
          <dd>{ship.attack ? `${ship.attack}/day` : 'unarmed'}</dd>
        </div>
        {ship.path && ship.path.length > 0 ? (
          <div className="stat">
            <dt>Jumps left</dt>
            <dd>{ship.path.length + (ship.phase === 'hyperlane' ? 1 : 0)}</dd>
          </div>
        ) : null}
      </dl>
      {mine && match ? (
        <button
          type="button"
          className="travel-btn"
          onClick={() => match.setSelection(selected ? match.selection.filter((id) => id !== ship.id) : [...match.selection, ship.id])}
        >
          {selected ? 'Remove from selection' : 'Select for orders'}
        </button>
      ) : null}
    </aside>
  );
}

// ─── end of match ───

export function GameOver() {
  const sim = useSim();
  const match = useMatch();
  const [hidden, setHidden] = useState(false);
  if (!sim?.over || !match || hidden) return null;
  const winner = (sim.empires ?? []).find((e) => e.id === sim.winner);
  const won = !!match.you && sim.winner === match.you;
  const last = [...(sim.events ?? [])].reverse().find((e) => e.kind === 'victory');
  return (
    <div className="gameover">
      <div className="gameover-panel">
        <h1 style={{ color: winner?.color }}>{won ? 'Victory' : match.you ? 'Defeat' : 'Game over'}</h1>
        <p>{last?.text ?? (winner ? `${winner.name} wins.` : 'No empire survived.')}</p>
        <div className="gameover-actions">
          <button type="button" className="login-btn login-btn-primary" onClick={() => gotoMatch(null)}>
            Back to games
          </button>
          <button type="button" className="login-btn" onClick={() => setHidden(true)}>
            Look around
          </button>
        </div>
      </div>
    </div>
  );
}
